<template>
  <page-main
    title="Fit & Preferences"
    nextText="Career Profile"
    buttonTabIndex="99"
    eventName="nav"
    @nav="nav('/CareerProfile')"
    @save="save()"
  >
    <template v-slot:aside>
      Answer a few easy questions. This helps recruiters and HR match you better
      and de-risk internal moves.
      <ul>
        <li>
          Your answers are saved automatically when you leave the page.
        </li>
        <li>
          You can come back at any time to update your preferences as your goals
          change.
        </li>
      </ul>
    </template>
    <template v-slot:nav>
      <v-system-bar color="accent" height="5" class="mt-4"></v-system-bar>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <r-tip-container :tips="tips"></r-tip-container>
      </modal>
    </template>
    <v-container>
      <v-row v-for="(question, index) in questions || []" :key="question.id">
        <v-col cols="12" class="r-col">
          <r-autocomplete
            :value="getSelectionFor(question)"
            :items="question.options || []"
            :title-text="question.label"
            :helper-text="question.helper || 'Select your preferences'"
            label="Type to search"
            multiple
            :tabindex="String(index + 1)"
            @input="val => onSelectionChange(question, val)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="r-col">
          <div class="text-subtitle-1 font-weight-medium">
            Anything we missed that's critical to your fit? (optional 1-2
            sentences)
          </div>
          <div class="text-body-2 grey--text mt-1 mb-2">
            Keep it short so it's easy to use.
          </div>
          <v-textarea
            v-model="additionalNotes"
            :placeholder="notesPlaceholder"
            :counter="notesMaxLength"
            :maxlength="notesMaxLength"
            outlined
            auto-grow
            rows="4"
            :tabindex="(questions || []).length + 1"
          />
        </v-col>
      </v-row>
    </v-container>
  </page-main>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { mapFields } from "vuex-map-fields";
import Modal from "@/components/Modal.vue";

export default {
  name: "FitPreferences",
  components: { Modal },
  data: () => ({}),
  computed: {
    ...mapFields("fitPreferences", {
      storeAnswers: "answers",
      storeAdditionalNotes: "additionalNotes"
    }),
    ...mapState("fitPreferences", ["questions", "additionalNotesConfig"]),
    additionalNotes: {
      get() {
        return this.storeAdditionalNotes;
      },
      set(value) {
        this.storeAdditionalNotes = value;
      }
    },
    notesMaxLength() {
      return this.additionalNotesConfig?.maxLength ?? 240;
    },
    notesPlaceholder() {
      return (
        this.additionalNotesConfig?.placeholder ||
        "Prefer builder culture with clear goals; will pass on weak bar or slow decisions."
      );
    },
    tips() {
      return [
        "Answer honestly - these preferences help find a match that sticks, not one that looks good on paper.",
        "You can select as many options per question as genuinely apply. Leaving a question blank is fine if none fit.",
        "Use the notes field for anything that doesn't fit the dropdowns, like a specific dealbreaker or a nuance you want a recruiter to see."
      ];
    }
  },
  async mounted() {
    await this.loadFitPreferencesQuestions();
    await this.loadFitPreferences();
  },
  methods: {
    ...mapActions("fitPreferences", [
      "loadFitPreferences",
      "saveFitPreferences",
      "loadFitPreferencesQuestions"
    ]),
    nav(routerPath) {
      this.$router.push({ path: routerPath });
    },
    async save() {
      await this.saveFitPreferences();
    },
    getSelectionFor(question) {
      const stored = (this.storeAnswers || []).find(
        a => a.questionId === question.id
      );
      if (!stored) return [];
      const options = question.options || [];
      return (stored.selectedOptions || [])
        .map(opt => {
          if (opt?.id != null) {
            const match = options.find(o => o.id === opt.id);
            if (match) return match;
          }
          const label = (opt?.label || "").trim();
          if (!label) return null;
          return { id: opt?.id ?? null, label };
        })
        .filter(x => x !== null);
    },
    onSelectionChange(question, value) {
      const normalized = [];
      const seen = new Set();
      for (const item of value || []) {
        if (!item) continue;
        if (typeof item === "string") continue;
        const label = (item.label || "").trim();
        const id = item.id ?? null;
        if (!label && id == null) continue;
        const key = id != null ? `id:${id}` : `label:${label.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        normalized.push({ id, label });
      }
      this.$store.commit("fitPreferences/setAnswerForQuestion", {
        questionId: question.id,
        selectedOptions: normalized
      });
    }
  }
};
</script>
